import SectionHead from "./SectionHead";
import { topics } from "@/mocks/social";
import type { TopicCard } from "@/mocks/social";

const statusStyle: Record<string, string> = {
  hot: "bg-primary-100 text-primary-700",
  mid: "bg-accent-100 text-accent-800",
  cool: "bg-secondary-100 text-secondary-800",
};

const statusDot: Record<string, string> = {
  hot: "bg-primary-500",
  mid: "bg-accent-500",
  cool: "bg-secondary-400",
};

function MiniCard({ t }: { t: TopicCard }) {
  return (
    <article className="flex h-full flex-col rounded-lg border border-secondary-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-mono text-secondary-400">{t.code}</span>
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusStyle[t.statusTone]}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${statusDot[t.statusTone]}`} />
          {t.status}
        </span>
      </div>
      <h4 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-[17px]">
        {t.title}
      </h4>
      <p className="mt-1.5 line-clamp-2 text-[13px] leading-5 text-secondary-600">{t.brief}</p>
      <div className="mt-auto pt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-secondary-500">
          <span>发酵进度</span>
          <span className="font-bold text-foreground-700">{t.progress}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary-200">
          <div className="h-full rounded-full bg-primary-500" style={{ width: `${t.progress}%` }} />
        </div>
      </div>
    </article>
  );
}

export default function TopicGrid() {
  const featured = topics.find((t) => t.featured)!;
  const rest = topics.filter((t) => !t.featured);

  return (
    <section id="topics" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-16">
        <SectionHead
          no="01"
          kicker="在追专题库 · TRACKING"
          title="本网在追 · 谁的瓜还热着"
          sub="进入深度发酵的专题按进度持续跟更，口径与证据链逐条留痕。"
        />

        <div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
          <article className="group flex flex-col overflow-hidden rounded-lg border border-secondary-200 bg-background-50 lg:col-span-2">
            <div className="relative h-52 w-full overflow-hidden md:h-64 lg:h-72">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20documents%20and%20contract%20papers%20viewed%20under%20a%20large%20glowing%20magnifying%20glass%20on%20a%20dark%20ink%20desk%2C%20warm%20amber%20desk%20lamp%20light%2C%20scattered%20coins%20and%20stamped%20red%20seals%2C%20moody%20high-contrast%20editorial%20magazine%20art%2C%20grainy%20film%20texture%2C%20no%20people%2C%20no%20readable%20text%2C%20dramatic%20composition&width=1280&height=640&seq=hei-liao-feature-2026-02&orientation=landscape"
                alt="录音门母带证据链专题配图"
                title="录音门发酵专题母带证据链配图"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent" />
              <div className="absolute bottom-3 left-4 flex items-center gap-2 text-xs font-semibold text-background-50">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-2.5 py-1">
                  <i className="ri-fire-fill text-sm" />热度 {featured.hot}
                </span>
                <span className="rounded-full bg-foreground-950/60 px-2.5 py-1 backdrop-blur">{featured.updates}</span>
              </div>
              <span className={`absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[featured.statusTone]}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${statusDot[featured.statusTone]}`} />
                {featured.status}
              </span>
            </div>
            <div className="flex flex-1 flex-col p-4 md:p-6">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                <span className="text-secondary-400">{featured.code}</span>
                {featured.tags.map((tag) => (
                  <span key={tag} className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-0.5 text-secondary-700">
                    #{tag}
                  </span>
                ))}
              </div>
              <h4 className="font-heading mt-2.5 text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                {featured.title}
              </h4>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-secondary-600">{featured.brief}</p>
              <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-secondary-200 pt-4">
                <div className="flex items-center gap-2 text-xs text-secondary-500">
                  <i className="ri-bar-chart-line text-sm" />发酵进度
                  <span className="font-bold text-foreground-800">{featured.progress}%</span>
                </div>
                <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-secondary-200">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${featured.progress}%` }} />
                </div>
                <a
                  href="#track"
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-3.5 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入追踪线
                  <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            </div>
          </article>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-1">
            {rest.slice(0, 2).map((t) => (
              <MiniCard key={t.code} t={t} />
            ))}
          </div>

          {rest.slice(2).map((t) => (
            <div key={t.code} className="lg:col-span-1">
              <MiniCard t={t} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}